import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { FC, useState } from "react";
import moment from "moment";
import { replace } from "lodash";
import { useHistory } from "react-router-dom";
import { Icon } from "@/components";
import { Badge, IconButton } from "@/ui";
import menuStyles from "@/ui/components/ButtonDropdown/ButtonDropdown.module.scss";
import { convertTZ, timeWindowFormat } from "@/utils";
import { useStoreContext } from "context";
import routes from "config/routes/routes";
import { AlgoliaOrder } from "types/algolia";
import badgeStyles from "../orderBadges.module.scss";
import {
    getOrderTypeTone,
    normalizeStatusTone,
    type OrderStatusTone,
} from "../orderVisualTokens";
import styles from "./KanbanCard.module.scss";

export type KanbanCardAccent = OrderStatusTone | "metalic" | string;

export type KanbanCardViewProps = {
    order: AlgoliaOrder;
    accentClass: KanbanCardAccent;
    /** IANA timezone used to format reservation window. */
    timezone?: string;
    onOpen: (order: AlgoliaOrder) => void;
    onOpenDetails: (id: string) => void;
};

export type KanbanCardProps = {
    order: AlgoliaOrder;
    accentClass: KanbanCardAccent;
    handleModalToggle: (order: AlgoliaOrder) => void;
};

const MoreIcon = () => <Icon icon="more" />;

/**
 * Presentational kanban card — used by Storybook and by the connected wrapper.
 * Keeps formatting / UI only; navigation and store context stay in `KanbanCard`.
 */
export const KanbanCardView: FC<KanbanCardViewProps> = ({
    order,
    accentClass,
    timezone,
    onOpen,
    onOpenDetails,
}) => {
    const [isOptionsPopupOpen, setIsOptionsPopupOpen] = useState(false);
    const statusTone = normalizeStatusTone(accentClass);
    const typeTone = getOrderTypeTone(order.type);

    const utcReservationWindow = convertTZ(
        order?.reservation_start_window,
        timezone as string,
    );

    const datetime = `${moment(utcReservationWindow).format("MMMM Do")}`;

    return (
        <div className={styles.group}>
            <button
                className={`${styles.card} ${badgeStyles.statusAccent}`}
                data-accent={statusTone}
                data-menu-open={isOptionsPopupOpen ? "true" : undefined}
                onClick={() => onOpen(order)}
                type="button"
            >
                <div className={styles.header}>
                    <h4 className={styles.customer}>{order.customer}</h4>
                </div>
                <p className={styles.city}>{order.city}</p>
                <p
                    className={styles.datetime}
                >{`${datetime},  ${timeWindowFormat(
                    order.delivery_time_window,
                )}`}</p>
                <div className={styles.orderInfo}>
                    <Badge
                        className={badgeStyles.typeBadge}
                        data-tone={typeTone}
                        variant="neutral"
                    >
                        {order.type}
                    </Badge>
                    <p className={styles.price}>{order.total_price}</p>
                </div>
            </button>
            <div className={styles.menu} data-accent={statusTone}>
                <DropdownMenu.Root
                    onOpenChange={setIsOptionsPopupOpen}
                    open={isOptionsPopupOpen}
                >
                    <DropdownMenu.Trigger asChild>
                        <IconButton
                            aria-label="Order actions"
                            onClick={event => event.stopPropagation()}
                            size="sm"
                            type="button"
                            variant="tertiary"
                        >
                            <MoreIcon />
                        </IconButton>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.Content
                            align="end"
                            className={`${menuStyles.content} ${styles.menuContent}`}
                            data-accent={statusTone}
                            side="bottom"
                            sideOffset={4}
                        >
                            <DropdownMenu.Label className={styles.menuTitle}>
                                Actions
                            </DropdownMenu.Label>
                            <DropdownMenu.Item
                                className={menuStyles.item}
                                onSelect={() => onOpenDetails(order.objectID)}
                            >
                                Details
                            </DropdownMenu.Item>
                        </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            </div>
        </div>
    );
};

const KanbanCard: FC<KanbanCardProps> = ({
    order,
    handleModalToggle,
    accentClass,
}) => {
    const history = useHistory();
    const { store } = useStoreContext();

    const handleDetailOpen = (id: string) => {
        const url = replace(routes.ORDER_DETAIL.route, ":id", id);
        history.push(url);
    };

    return (
        <KanbanCardView
            accentClass={accentClass}
            onOpen={handleModalToggle}
            onOpenDetails={handleDetailOpen}
            order={order}
            timezone={store?.timezone}
        />
    );
};

export default KanbanCard;
